import {
  Activity,
  Archive,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRightLeft,
  ArrowUpFromLine,
  ArrowUpRight,
  Award,
  Banknote,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  CircleX,
  Copy,
  Download,
  EllipsisVertical,
  Eye,
  EyeOff,
  FileBarChart,
  FilePen,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  Funnel,
  GitMerge,
  Grid2x2,
  History,
  IdCard,
  Info,
  KeyRound,
  Languages,
  Layers3,
  Link2,
  ListChecks,
  LoaderCircle,
  LogOut,
  MapPin,
  Moon,
  Network,
  Pencil,
  Phone,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  SearchX,
  Send,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  SquareCheckBig,
  Sun,
  ToggleLeft,
  Trash2,
  TriangleAlert,
  User,
  Users,
  UsersRound,
  Wallet,
  X,
  type LucideProps,
} from 'lucide-react';
import { createElement, type ComponentType } from 'react';
import { cn } from '@/lib/utils';

/**
 * The single icon source for the application (rule I1). Product code imports
 * from here and never from `lucide-react`, so one action always carries one
 * glyph and a swap happens in a single file.
 *
 * Names are the canonical lucide names of the installed version (rule I2).
 * Several entries in the icon table are spelled with names that lucide has
 * since retired into aliases; the canonical spelling is used instead and the
 * glyph is unchanged:
 *
 * | Icon table | Canonical name in lucide-react 1.x |
 * | ---------- | ---------------------------------- |
 * | `Filter`        | `Funnel`           |
 * | `MoreVertical`  | `EllipsisVertical` |
 * | `Loader2`       | `LoaderCircle`     |
 * | `CheckCircle2`  | `CircleCheck`      |
 * | `XCircle`       | `CircleX`          |
 * | `AlertTriangle` | `TriangleAlert`    |
 */

export type IconComponent = ComponentType<LucideProps>;

/** Rule I5. Pixel sizes, for callers that size an icon numerically. */
export const ICON_SIZE = {
  /** Inline and action icons. */
  inline: 16,
  /** Overflow menus and status icons. */
  status: 20,
  /** Empty states and dialog semantics. */
  display: 24,
} as const;

/** Rule I5 as Tailwind utilities, which is how components usually size icons. */
export const ICON_SIZE_CLASS = {
  inline: 'size-4',
  status: 'size-5',
  display: 'size-6',
} as const;

/**
 * Rule R4. The only place in the application that mirrors an icon for RTL.
 * It is a CSS variant rather than a direction lookup, so it also holds inside
 * portalled content that never re-renders on a locale change.
 */
export const MIRRORED_ICON_CLASS = 'rtl:-scale-x-100';

function decorate(Icon: IconComponent, extraClassName: string, displayName: string): IconComponent {
  function DecoratedIcon({ className, ...props }: LucideProps) {
    return createElement(Icon, { ...props, className: cn(extraClassName, className) });
  }
  DecoratedIcon.displayName = displayName;
  return DecoratedIcon;
}

/**
 * Wraps a directional icon so it mirrors with the document direction. Use this
 * instead of attaching an `rtl:` variant at the call site (rule R4). Icons that
 * must never mirror — brand marks, numerals, media controls (rule R5) — are
 * simply not wrapped.
 */
export function mirrorIcon(Icon: IconComponent, displayName: string): IconComponent {
  return decorate(Icon, MIRRORED_ICON_CLASS, displayName);
}

/** Create / add a business entity. */
export const IconCreate: IconComponent = Plus;
/** Edit an existing record. */
export const IconEdit: IconComponent = Pencil;
/** Delete a record. */
export const IconDelete: IconComponent = Trash2;
/** Save changes. */
export const IconSave: IconComponent = Save;
/** Confirm / submit a decision. */
export const IconConfirm: IconComponent = Check;
/** Cancel an action, and close a surface. */
export const IconCancel: IconComponent = X;
/** Search. */
export const IconSearch: IconComponent = Search;
/** Open or apply filters. */
export const IconFilter: IconComponent = Funnel;
/** Clear every active filter. */
export const IconResetFilters: IconComponent = RotateCcw;
/** Row and toolbar overflow menu. */
export const IconOverflow: IconComponent = EllipsisVertical;
/** View the details of a record. */
export const IconViewDetails: IconComponent = Eye;
/** Continue a saved draft transfer (KAN-58): row action and the الحوالات المبدئية leaf. */
export const IconContinueDraft: IconComponent = FilePen;
/** Print. */
export const IconPrint: IconComponent = Printer;
/** Export to a spreadsheet. */
export const IconExportSpreadsheet: IconComponent = FileSpreadsheet;
/** Export to a PDF document. */
export const IconExportPdf: IconComponent = FileText;
/** Download a file. */
export const IconDownload: IconComponent = Download;
/** Refresh, or change state. */
export const IconRefresh: IconComponent = RefreshCw;
/** Send / post. */
export const IconSend: IconComponent = Send;
/** Navigate back. Mirrors with the direction. */
export const IconBack: IconComponent = mirrorIcon(ArrowLeft, 'IconBack');
/** Navigate to a related record. Mirrors with the direction. */
export const IconNavigateToRecord: IconComponent = mirrorIcon(ArrowUpRight, 'IconNavigateToRecord');
/** Busy. Carries its own animation so no caller has to remember it. */
export const IconBusy: IconComponent = decorate(LoaderCircle, 'animate-spin', 'IconBusy');
/** Success status. */
export const IconSuccess: IconComponent = CircleCheck;
/** Error status. */
export const IconError: IconComponent = CircleX;
/** Warning status. */
export const IconWarning: IconComponent = TriangleAlert;
/** Information status. */
export const IconInfo: IconComponent = Info;

/**
 * Additional icons for navigation, domain concepts and lifecycle actions that
 * appear in product code but are not one of the 23 actions catalogued above
 * (rule I3 only names those). They still route through this module, so rule
 * I1 continues to hold — lucide-react is imported nowhere else in product
 * code. None of these belong in `ICONS`/`ICON_NAMES`, which mirror the
 * UI/UX principles table exactly and nothing more.
 */
/** Language switch. */
export const IconLanguage: IconComponent = Languages;
/** Switch to the dark theme. */
export const IconThemeDark: IconComponent = Moon;
/** Switch to the light theme. */
export const IconThemeLight: IconComponent = Sun;
/** Identity: review center, identity-conflict cases. */
export const IconIdentity: IconComponent = Fingerprint;
/** Information-completeness and reliability classification. */
export const IconClassification: IconComponent = Layers3;
/** A connected integration source. */
export const IconIntegration: IconComponent = Network;
/** Permission groups and access control. */
export const IconAccess: IconComponent = ShieldCheck;
/** The customer directory. */
export const IconCustomers: IconComponent = UsersRound;
/** Expands a trigger's attached menu. */
export const IconMenuExpand: IconComponent = ChevronsUpDown;
/** Sign out. */
export const IconLogout: IconComponent = LogOut;
/** Generic user / account avatar placeholder. */
export const IconUser: IconComponent = User;
/** A branch or physical location. */
export const IconLocation: IconComponent = MapPin;
/** Merge two records. */
export const IconMerge: IconComponent = GitMerge;
/** An audit or reliability history entry. */
export const IconHistory: IconComponent = History;
/** An integration source's API credential. */
export const IconApiKey: IconComponent = KeyRound;
/** A linked or associated record. */
export const IconLink: IconComponent = Link2;
/** No results found. */
export const IconNoResults: IconComponent = SearchX;
/**
 * Restore a previously removed record. Shares its glyph with the
 * catalogued reset-filters action (both read as "revert"), but names a
 * distinct lifecycle action; reset-filters has no current call site.
 */
export const IconRestore: IconComponent = RotateCcw;
/**
 * Archive (soft-delete) a record. Decision of record 5: soft delete is a
 * reversible hide, not destruction, so it is deliberately not `IconDelete`
 * (`Trash2`) - a distinct glyph carries the distinction the same way the
 * copy does, rather than relying on the label alone.
 */
export const IconArchive: IconComponent = Archive;
/** Previous page. Mirrors with the direction. */
export const IconPreviousPage: IconComponent = mirrorIcon(ChevronLeft, 'IconPreviousPage');
/** Next page. Mirrors with the direction. */
export const IconNextPage: IconComponent = mirrorIcon(ChevronRight, 'IconNextPage');
/**
 * Rule L8: the shared data table's active-sort indicator for a column
 * (rotated for descending). Not a directional icon (rule R4) - a chevron
 * pointing at data, not at reading order - so it is not mirror-wrapped.
 */
export const IconExpandToggle: IconComponent = ChevronDown;
/**
 * The shared data table's sortable-but-inactive column indicator. Shares its
 * glyph with `IconMenuExpand` (both read as "this can be expanded/ordered"),
 * the same way the template's own icon table reuses one glyph for both.
 */
export const IconSortable: IconComponent = ChevronsUpDown;
/**
 * Rule NV8: a sidebar parent's disclosure affordance. Points toward the
 * inline-end edge while its group is closed, and rotates in place to point
 * down once it opens - a CSS-only pair keyed on the ancestor `Collapsible`'s
 * own `data-state`, so it never depends on reading the document direction in
 * script the way the rejected reference does (section 17). Ported from the
 * template's own `IconNavDisclosure`
 * (`nest-react-monorepo/apps/web/src/lib/icons.ts`). Positioning (`ms-auto`)
 * is the call site's job, same as any other icon.
 */
export const IconNavDisclosure: IconComponent = decorate(
  ChevronRight,
  'transition-transform group-data-[state=closed]/collapsible:rtl:-scale-x-100 group-data-[state=open]/collapsible:rotate-90',
  'IconNavDisclosure',
);
/** Sidebar sub-item: permission groups. */
export const IconGroup: IconComponent = Users;
/** Sidebar sub-item: group memberships and their scope. */
export const IconMembership: IconComponent = IdCard;
/** Sidebar sub-item: automatic information-completeness configuration. */
export const IconCompleteness: IconComponent = ListChecks;
/** Sidebar sub-item: manual reliability categories. */
export const IconReliabilityCategory: IconComponent = Award;
/** Sidebar sub-item: the integrations connection/monitor status view. */
export const IconConnectionStatus: IconComponent = Activity;
/** Sidebar section: settings (Users, Groups). */
export const IconSettings: IconComponent = Settings;
/** Sidebar sub-item and page: categories (التصنيفات). */
export const IconCategory: IconComponent = IconClassification;
/** Assign services page: the services tree and its "select all" affordance. */
export const IconServices: IconComponent = SquareCheckBig;
/** User menu: "الأنظمة" (back to the Unified Access launcher). */
export const IconApplications: IconComponent = Grid2x2;
/** User menu: "تغيير الفرع" (change branch). */
export const IconBranch: IconComponent = Building2;
/** Copy-to-clipboard affordance (one-time password reset link). */
export const IconCopy: IconComponent = Copy;
/** Show/hide toggles, e.g. an unmasked document number. */
export const IconShow: IconComponent = Eye;
export const IconHide: IconComponent = EyeOff;
/** Customer drawer: phone field. */
export const IconPhone: IconComponent = Phone;
/** Row action: تغيير الحالة (activate/deactivate). */
export const IconStatus: IconComponent = ToggleLeft;
/** Sidebar sub-item: registered integration source systems. */
export const IconConnectedSystems: IconComponent = Server;
/** Sidebar sub-item and page: شرائح العمولة (commission tiers, from-to ranges). */
export const IconTier: IconComponent = SlidersHorizontal;
/** Sidebar sub-item and page: إعدادات الحوالات (the receipt's own settings). */
export const IconTransferSettings: IconComponent = Receipt;
/** Sidebar leaf and page: الصناديق. */
export const IconBox: IconComponent = Wallet;
/** Sidebar leaf and page: الحسابات البنكية. */
export const IconBankAccount: IconComponent = Banknote;
/** Row action: إضافة رصيد. */
export const IconBalanceIn: IconComponent = ArrowDownToLine;
/** Row action: سحب رصيد. */
export const IconBalanceOut: IconComponent = ArrowUpFromLine;
/** Row action: نقل رصيد. */
export const IconBalanceMove: IconComponent = ArrowRightLeft;
/** Row action and page: كشف الحركات. */
export const IconLedger: IconComponent = IconHistory;
/** Sidebar leaf and page: التقارير. */
export const IconReports: IconComponent = FileBarChart;

/** The icon table of the UI/UX principles, in the order it is written there. */
export const ICONS = {
  create: IconCreate,
  edit: IconEdit,
  delete: IconDelete,
  save: IconSave,
  confirm: IconConfirm,
  cancel: IconCancel,
  search: IconSearch,
  filter: IconFilter,
  resetFilters: IconResetFilters,
  overflow: IconOverflow,
  viewDetails: IconViewDetails,
  print: IconPrint,
  exportSpreadsheet: IconExportSpreadsheet,
  download: IconDownload,
  refresh: IconRefresh,
  send: IconSend,
  back: IconBack,
  navigateToRecord: IconNavigateToRecord,
  busy: IconBusy,
  success: IconSuccess,
  error: IconError,
  warning: IconWarning,
  info: IconInfo,
} as const satisfies Record<string, IconComponent>;

export type IconName = keyof typeof ICONS;

/** Every semantic action the icon table covers, in table order. */
export const ICON_NAMES = Object.keys(ICONS) as readonly IconName[];
